import {
  AddRounded,
  CheckRounded,
  DeleteOutline,
  FilterAltOutlined,
  KeyboardArrowDown,
  PersonOutlineOutlined,
  ReplayRounded,
  SaveAsOutlined,
  StarOutlineRounded,
  StarRounded,
  SubdirectoryArrowRight,
  SwapVertOutlined,
  ViewComfyOutlined,
} from "@mui/icons-material";
import {
  Badge,
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import size from "lodash/size";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import ButtonMenu from "../../components/ButtonMenu";
import SlowRequestPrompt from "../../components/SlowRequestPrompt/SlowRequestPrompt";
import SearchInput from "../../components/inputs/SearchInput";
import FilterTabSwitcher from "../../components/navigation/FilterTabSwitcher";
import Flx from "../../components/layout/Flx";
import ScreenContent from "../../components/layout/ScreenContent";
import BasicModal from "../../components/modals/BasicModal";
import StyledMenu from "../../components/StyledMenu";
import Htag from "../../components/typography/Htag";
import Txt from "../../components/typography/Txt";
import { useUserPreferences } from "../../hooks/utils/useUserPreferences";
import { nsKey } from "../../utils/storage/partnerStorage";
import { drilldownBaseForRow } from "../../pipelines/pipelineConfig";
import { level1 } from "../../theme/colors";
import {
  DownloadDropdownButton,
  LoanOfficerPipelineTable,
  LOAN_SUBSTATUS_MAP,
  SORT_MENU_OPTIONS,
  SORT_OPTIONS,
} from "../LoanOfficerPipelineScreen/LoanOfficerPipelineScreen";

// ════════════════════════════════════════════════════════════════
// PipelineTableScreen
// A trimmed, reusable version of LoanOfficerPipelineScreen: just the
// toolbar (search + sort + layout + export) and the AG Grid table.
// Always rendered in compact mode (no compact toggle). Each instance gets
// its own layout storage namespace via `storageKeyPrefix`, and its own data
// source via `fetchData`, so Pre-Close / Closing / Post-Close don't collide.
// ════════════════════════════════════════════════════════════════

// ─── "Show only my loans" ────────────────────────────────────────────────────
// A loan is "mine" if the current user's email appears on any assignee/role
// field. String-email fields + array-of-email fields are checked separately.
// Lendmarq divergence (see CLAUDE.md #1): LQ's role set is salesperson /
// processor / underwriter / loanCloser / salesAnalyst — no accountManager or
// structurer (those are Eastview-only). Don't let a sync re-add them.
const MY_LOAN_EMAIL_FIELDS = [
  // Lendmarq divergence (see CLAUDE.md #1): LQ role set only — no EV-only
  // preUnderwriter / qc_analyst (and no accountManager / structurer).
  "salesperson",
  "processor",
  "underwriter",
  "loanCloser",
  "salesAnalyst",
  "createdBy",
];
const MY_LOAN_EMAIL_ARRAY_FIELDS = ["permittedUsers", "subscribedUsers"];

const loanBelongsToUser = (loan, email) => {
  if (!loan || !email) return false;
  const lc = email.toLowerCase();
  const matches = (v) => typeof v === "string" && v.toLowerCase() === lc;
  if (MY_LOAN_EMAIL_FIELDS.some((f) => matches(loan[f]))) return true;
  return MY_LOAN_EMAIL_ARRAY_FIELDS.some(
    (f) => Array.isArray(loan[f]) && loan[f].some(matches),
  );
};

const readJson = (raw, fallback) => {
  try {
    const v = JSON.parse(raw);
    return isNil(v) ? fallback : v;
  } catch {
    return fallback;
  }
};

// A saved layout predates any column added to the board since it was saved, and
// applyColumnState({ applyOrder: true }) parks such columns at the far right.
// Slot each one in after its nearest default-order neighbour instead, with its
// default visibility, so a new column shows up where the board defines it.
const mergeNewColumnsIntoState = (columnState, columnDefs) => {
  if (!Array.isArray(columnState) || !Array.isArray(columnDefs)) {
    return columnState;
  }
  const defaults = columnDefs
    .map((d) => ({ colId: d?.colId ?? d?.field, hide: !!d?.hide }))
    .filter((d) => d.colId);
  const merged = [...columnState];
  const has = new Set(merged.map((s) => s.colId));
  defaults.forEach((d, i) => {
    if (has.has(d.colId)) return;
    let at = 0;
    for (let j = i - 1; j >= 0; j -= 1) {
      const k = merged.findIndex((s) => s.colId === defaults[j].colId);
      if (k !== -1) {
        at = k + 1;
        break;
      }
    }
    merged.splice(at, 0, { colId: d.colId, hide: d.hide });
    has.add(d.colId);
  });
  return merged;
};

// A comparable fingerprint of the grid's column state, for telling whether the
// table still looks the way its column defs set it up. Widths are left out:
// flex columns follow the window and the shared table re-sizes Comments itself
// after load, so a width diff isn't a user change — hand resizes are tracked
// from the columnResized event instead.
const columnStateSignature = (columnState) =>
  JSON.stringify(
    (Array.isArray(columnState) ? columnState : []).map((s) => [
      s.colId,
      !!s.hide,
      s.pinned ?? null,
      s.sort ?? null,
      s.sortIndex ?? null,
      s.rowGroup ? (s.rowGroupIndex ?? 0) : null,
      s.pivot ? (s.pivotIndex ?? 0) : null,
      s.aggFunc ?? null,
    ]),
  );

// ─── Prefix-scoped layout persistence ───────────────────────────────────────
// `layoutScope` namespaces the saved-layouts LIST, which boards can share (see
// BOARD_LAYOUT_SCOPE); everything else — the default-layout pointer, the view
// toggles, the session snapshot — stays keyed by `storageKeyPrefix` so each
// board keeps its own.
const makeLayoutStorage = (storageKeyPrefix, layoutScope) => {
  const LAYOUTS_KEY = nsKey(`${layoutScope || storageKeyPrefix}-layouts`);
  // Where this board's layouts lived before it joined a shared scope, plus a
  // flag marking that they've been folded in (see mergeLegacyLayouts).
  const LEGACY_LAYOUTS_KEY = nsKey(`${storageKeyPrefix}-layouts`);
  const LEGACY_MERGED_KEY = nsKey(`${storageKeyPrefix}-layouts-merged`);
  const DEFAULT_KEY = nsKey(`${storageKeyPrefix}-default-layout`);
  const MINE_KEY = nsKey(`${storageKeyPrefix}-show-only-mine`);
  // "Remember where I left off": a boolean toggle + the captured snapshot of
  // the last view (columns, sort, filters). Both are namespaced per board so
  // Pre-Close / Closing / Post-Close keep independent sessions.
  const REMEMBER_KEY = nsKey(`${storageKeyPrefix}-remember-session`);
  const SESSION_KEY = nsKey(`${storageKeyPrefix}-last-session`);

  const readLayoutList = (key) => {
    const v = readJson(localStorage.getItem(key), []);
    return Array.isArray(v) ? v : [];
  };

  // One-time migration for boards that moved onto a shared scope: fold the
  // views this board saved under its own key into the shared list, so nobody
  // loses layouts they'd already built. Runs once per board (the flag), which
  // also keeps a layout the user later deletes from being resurrected.
  const mergeLegacyLayouts = () => {
    if (LAYOUTS_KEY === LEGACY_LAYOUTS_KEY) return;
    if (localStorage.getItem(LEGACY_MERGED_KEY) === "true") return;
    const legacy = readLayoutList(LEGACY_LAYOUTS_KEY);
    if (legacy.length) {
      const shared = readLayoutList(LAYOUTS_KEY);
      const seen = new Set(shared.map((l) => l?.id));
      const merged = [
        ...shared,
        ...legacy.filter((l) => l?.id && !seen.has(l.id)),
      ];
      localStorage.setItem(LAYOUTS_KEY, JSON.stringify(merged));
    }
    localStorage.setItem(LEGACY_MERGED_KEY, "true");
  };

  return {
    getShowOnlyMine: () => localStorage.getItem(MINE_KEY) === "true",
    setShowOnlyMine: (v) =>
      localStorage.setItem(MINE_KEY, v ? "true" : "false"),
    getLayouts: () => {
      mergeLegacyLayouts();
      return readLayoutList(LAYOUTS_KEY);
    },
    saveLayouts: (l) =>
      localStorage.setItem(
        LAYOUTS_KEY,
        JSON.stringify(Array.isArray(l) ? l : []),
      ),
    getDefaultId: () => localStorage.getItem(DEFAULT_KEY) || null,
    setDefaultId: (id) => localStorage.setItem(DEFAULT_KEY, id ?? ""),
    // Defaults ON when the user hasn't set a preference yet; an explicit
    // "false" stored by toggling it off is still respected.
    getRememberSession: () => {
      const v = localStorage.getItem(REMEMBER_KEY);
      return v === null ? true : v === "true";
    },
    setRememberSession: (v) =>
      localStorage.setItem(REMEMBER_KEY, v ? "true" : "false"),
    getLastSession: () => readJson(localStorage.getItem(SESSION_KEY), null),
    setLastSession: (s) => {
      try {
        localStorage.setItem(SESSION_KEY, JSON.stringify(s ?? null));
      } catch {
        // best-effort convenience — ignore quota/serialization failures
      }
    },
    clearLastSession: () => localStorage.removeItem(SESSION_KEY),
  };
};

const PipelineTableScreen = ({
  // ({ onSuccessFn, onErrorFn }) => void — the data source for this table.
  fetchData,
  // Namespaces layout storage so each tab keeps independent saved views.
  storageKeyPrefix = "pipeline-table",
  // Optional separate namespace for the saved-layouts LIST, letting boards that
  // render the same columns share one set of views (Pre-Close + Post-Close pass
  // BOARD_LAYOUT_SCOPE). Defaults to storageKeyPrefix = views stay board-local.
  layoutScope,
  // When true, saved layouts + this board's default-layout pointer are pushed to
  // the user profile on every write (and hydrated back on a new device) via
  // useUserPreferences. Only meaningful for keys registered in its SYNCED_KEYS.
  syncLayoutsToProfile = false,
  // Optional column override; defaults to the standard pipeline columns.
  columnDefs,
  // Optional heading rendered above the toolbar.
  title,
  // Optional status switcher next to the search box. When provided, renders an
  // "All" + one-tab-per-status FilterTabSwitcher and slices the table by the
  // chosen status. Each entry is a status string (or { label, value }).
  statusOptions,
  // Row field the status switcher matches/counts against (e.g. "loanStatus"
  // for Pre-Close milestones, "subStatus" for Closing's Docs Out/Back).
  statusField = "loanStatus",
  // Widen the Loan Name column for these boards without touching the shared
  // column defs used by the main LO pipeline.
  loanNameMinWidth = 360,
  // Drilldown route base for row navigation. Preclose loans open at /loan/:id;
  // acquisitions (postclose) open the SAME drilldown at /acquisition/:id (see
  // src/pipelines/pipelineConfig.js).
  drilldownBase = "/loan",
  // Extra columns spliced in after Loan # (see LoanOfficerPipelineTable).
  extraColumnDefs,
}) => {
  const navigate = useNavigate();
  const userEmail = useSelector((state) => state?.user?.emailAddress);
  const gridRef = useRef(null);
  const storage = useMemo(
    () => makeLayoutStorage(storageKeyPrefix, layoutScope),
    [storageKeyPrefix, layoutScope],
  );
  const { syncPreferences } = useUserPreferences();
  // Push the layout keys to the profile after a write. No-op on boards that
  // haven't opted in, so they don't POST a user update on every layout change.
  const syncLayouts = useCallback(() => {
    if (syncLayoutsToProfile) syncPreferences?.();
  }, [syncLayoutsToProfile, syncPreferences]);

  const [rowData, setRowData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quickFilter, setQuickFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  // Sub-status picker: only relevant when the status switcher slices by
  // loanStatus AND the chosen status has sub-statuses (e.g. "Processing").
  const [subStatusFilter, setSubStatusFilter] = useState(null);
  const [showOnlyMine, setShowOnlyMine] = useState(() =>
    storage.getShowOnlyMine(),
  );
  const [activeSort, setActiveSort] = useState(null);

  const [layouts, setLayouts] = useState(() => storage.getLayouts());
  const [defaultId, setDefaultId] = useState(() => storage.getDefaultId());
  const [activeLayoutId, setActiveLayoutId] = useState(null);
  // "Remember where I left off" — when on, the live view is snapshotted on
  // every change and restored on the next visit (taking precedence over the
  // default layout).
  const [rememberSession, setRememberSession] = useState(() =>
    storage.getRememberSession(),
  );

  const layoutAnchorRef = useRef(null);
  const layoutMenuRef = useRef(null);
  const defaultAppliedRef = useRef(false);
  const fetchedRef = useRef(false);
  const gridReadyRef = useRef(false);
  // Suppresses session snapshots while we're programmatically restoring, so the
  // restore doesn't overwrite the saved snapshot with a half-applied state.
  const restoringSessionRef = useRef(false);
  // The untouched table's column fingerprint (set on grid ready), and whether
  // the live view has drifted from it — drives the "Reset view" button.
  const pristineColumnStateRef = useRef(null);
  const userResizedRef = useRef(false);
  const [isViewChanged, setIsViewChanged] = useState(false);

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveDialogName, setSaveDialogName] = useState("");

  // ── Data fetch on mount ──
  const loadData = useCallback(() => {
    if (typeof fetchData !== "function") return;
    setLoading(true);
    const onError = (err) => {
      console.error("PipelineTableScreen fetch error:", err);
      setLoading(false);
    };
    fetchData({
      onSuccessFn: (data) => {
        setRowData(Array.isArray(data) ? data : []);
        setLoading(false);
      },
      // Different pipeline hooks name the error callback differently
      // (onFailFn vs onErrorFn) — pass both so either is honored.
      onFailFn: onError,
      onErrorFn: onError,
    });
  }, [fetchData]);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    loadData();
  }, [loadData]);

  // ── Status switcher ──
  const normalizedStatusOptions = useMemo(
    () =>
      (Array.isArray(statusOptions) ? statusOptions : []).map((o) =>
        typeof o === "string" ? { label: o, value: o } : o,
      ),
    [statusOptions],
  );

  const statusTabs = useMemo(() => {
    if (isEmpty(normalizedStatusOptions)) return null;
    let rows = Array.isArray(rowData) ? rowData : [];
    // When "show only my loans" is active, the status counts should reflect
    // the same scoped set the grid shows (not the full pipeline).
    if (showOnlyMine) {
      rows = rows.filter((r) => loanBelongsToUser(r, userEmail));
    }
    return [
      { label: "All", value: null, count: size(rows) },
      ...normalizedStatusOptions.map((o) => ({
        label: o.label,
        value: o.value,
        count: rows.filter((r) => r?.[statusField] === o.value).length,
      })),
    ];
  }, [normalizedStatusOptions, rowData, statusField, showOnlyMine, userEmail]);

  // Sub-statuses for the currently selected status — only when the switcher
  // slices by loanStatus and the chosen status actually has sub-statuses.
  const subStatusesForStatus = useMemo(() => {
    if (statusField !== "loanStatus" || isNil(statusFilter)) return null;
    const subs = LOAN_SUBSTATUS_MAP[statusFilter];
    return Array.isArray(subs) && subs.length ? subs : null;
  }, [statusField, statusFilter]);

  const subStatusTabs = useMemo(() => {
    if (!subStatusesForStatus) return null;
    let rows = Array.isArray(rowData) ? rowData : [];
    if (showOnlyMine) {
      rows = rows.filter((r) => loanBelongsToUser(r, userEmail));
    }
    // Scope to loans in the selected parent status before counting sub-statuses.
    rows = rows.filter((r) => r?.[statusField] === statusFilter);
    return [
      { label: "All", value: null, count: size(rows) },
      ...subStatusesForStatus.map((sub) => ({
        label: sub,
        value: sub,
        count: rows.filter((r) => r?.subStatus === sub).length,
      })),
    ];
  }, [
    subStatusesForStatus,
    rowData,
    statusField,
    statusFilter,
    showOnlyMine,
    userEmail,
  ]);

  // Clear the sub-status selection whenever the parent status changes (or the
  // new status has no sub-statuses).
  useEffect(() => {
    setSubStatusFilter(null);
  }, [statusFilter]);

  const filteredRowData = useMemo(() => {
    if (!Array.isArray(rowData)) return rowData;
    let rows = rowData;
    if (!isNil(statusFilter)) {
      rows = rows.filter((r) => r?.[statusField] === statusFilter);
    }
    if (subStatusesForStatus && !isNil(subStatusFilter)) {
      rows = rows.filter((r) => r?.subStatus === subStatusFilter);
    }
    if (showOnlyMine) {
      rows = rows.filter((r) => loanBelongsToUser(r, userEmail));
    }
    return rows;
  }, [
    rowData,
    statusFilter,
    statusField,
    subStatusesForStatus,
    subStatusFilter,
    showOnlyMine,
    userEmail,
  ]);

  // Count of loans assigned to the current user across the whole board (before
  // status / sub-status / quick filters), surfaced in the "Show only my loans"
  // button label. Mirrors the LO pipeline's `assignedCount`.
  const myLoansCount = useMemo(
    () =>
      Array.isArray(rowData)
        ? rowData.filter((r) => loanBelongsToUser(r, userEmail)).length
        : 0,
    [rowData, userEmail],
  );

  // ── Sort ──
  const activeSortValue = useMemo(
    () => (activeSort ? `${activeSort.field}-${activeSort.sort}` : null),
    [activeSort],
  );

  const handleSortChange = useCallback((val) => {
    const api = gridRef.current?.api;
    if (!api) return;
    if (!val) {
      api.applyColumnState({ defaultState: { sort: null } });
      setActiveSort(null);
      return;
    }
    const sortOption = SORT_OPTIONS.find((o) => `${o.field}-${o.sort}` === val);
    if (!sortOption) return;
    api.applyColumnState({
      state: [{ colId: sortOption.field, sort: sortOption.sort }],
      defaultState: { sort: null },
    });
    setActiveSort(sortOption);
  }, []);

  // ── Layouts ──
  const persistLayouts = useCallback(
    (next) => {
      const safe = Array.isArray(next) ? next : [];
      setLayouts(safe);
      storage.saveLayouts(safe);
      syncLayouts();
    },
    [storage, syncLayouts],
  );

  // Every default-layout write goes through here so the profile sync can't be
  // forgotten at one of the five call sites that set it.
  const persistDefaultId = useCallback(
    (id) => {
      setDefaultId(id);
      storage.setDefaultId(id);
      syncLayouts();
    },
    [storage, syncLayouts],
  );

  // The profile → localStorage hydration in useUserPreferences lands after this
  // component has already read localStorage into state, so re-read on its event.
  // (It only fires when the profile actually filled in a missing key.)
  useEffect(() => {
    const onHydrated = () => {
      setLayouts(storage.getLayouts());
      setDefaultId(storage.getDefaultId());
    };
    window.addEventListener("preferences-hydrated", onHydrated);
    return () => window.removeEventListener("preferences-hydrated", onHydrated);
  }, [storage]);

  const captureCurrentState = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return null;
    return {
      columnState: api.getColumnState(),
      columnFilterModel: api.getFilterModel(),
      activeSort: activeSort || null,
      filters: { quickFilter: quickFilter ?? null },
    };
  }, [activeSort, quickFilter]);

  const applyLayout = useCallback(
    (layout, { skipAutoDefault = false } = {}) => {
      const api = gridRef.current?.api;
      if (!api || !layout) return;

      if (layout.columnState) {
        api.applyColumnState({
          state: mergeNewColumnsIntoState(layout.columnState, columnDefs),
          applyOrder: true,
        });
      }
      api.setFilterModel(layout.columnFilterModel ?? null);

      if (layout.activeSort?.field) {
        setActiveSort(layout.activeSort);
        api.applyColumnState({
          state: [
            { colId: layout.activeSort.field, sort: layout.activeSort.sort },
          ],
          defaultState: { sort: null },
        });
      } else {
        setActiveSort(null);
        // The saved columnState already carries the full sort state — including
        // header-click sorts that never go through the Sort dropdown (so
        // `activeSort` is null for them). Only force-clear sorts when there's no
        // columnState to restore them from; otherwise this wipes the sort the
        // applyColumnState above just restored.
        if (!Array.isArray(layout.columnState) || !layout.columnState.length) {
          api.applyColumnState({ defaultState: { sort: null } });
        }
      }

      setQuickFilter(layout.filters?.quickFilter ?? null);
      setActiveLayoutId(layout.id);

      if (!skipAutoDefault) {
        persistDefaultId(layout.id);
      }
    },
    [persistDefaultId, columnDefs],
  );

  // Snapshot the live view (grid state + the screen-level filters that drive
  // filteredRowData) for "Remember where I left off". Extends the layout shape
  // with a `screenFilters` bag so status / sub-status / show-only-mine
  // round-trip too.
  const captureSessionState = useCallback(() => {
    const base = captureCurrentState();
    if (!base) return null;
    return {
      ...base,
      screenFilters: {
        statusFilter: statusFilter ?? null,
        subStatusFilter: subStatusFilter ?? null,
        showOnlyMine: !!showOnlyMine,
      },
    };
  }, [captureCurrentState, statusFilter, subStatusFilter, showOnlyMine]);

  const persistSessionNow = useCallback(() => {
    if (!rememberSession) return;
    if (!gridReadyRef.current) return;
    if (restoringSessionRef.current) return;
    const state = captureSessionState();
    if (state) storage.setLastSession(state);
  }, [rememberSession, captureSessionState, storage]);

  const restoreSession = useCallback(
    (session) => {
      if (!session) return;
      const sf = session.screenFilters || {};
      setStatusFilter(sf.statusFilter ?? null);
      setSubStatusFilter(sf.subStatusFilter ?? null);
      if (typeof sf.showOnlyMine === "boolean") {
        setShowOnlyMine(sf.showOnlyMine);
        storage.setShowOnlyMine(sf.showOnlyMine);
      }
      // Grid columns / sort / header filters / search box.
      applyLayout(session, { skipAutoDefault: true });
    },
    [applyLayout, storage],
  );

  const handleToggleRememberSession = useCallback(() => {
    setRememberSession((prev) => {
      const next = !prev;
      storage.setRememberSession(next);
      if (next) {
        const state = captureSessionState();
        if (state) storage.setLastSession(state);
      } else {
        storage.clearLastSession();
      }
      return next;
    });
  }, [storage, captureSessionState]);

  // Persist whenever the screen-level filters change (these live in React state
  // and don't fire AG Grid's onStateUpdated). Grid-driven changes (columns,
  // sort, header filters) are captured via the table's onStateUpdated below.
  useEffect(() => {
    persistSessionNow();
  }, [
    statusFilter,
    subStatusFilter,
    showOnlyMine,
    activeSort,
    quickFilter,
    persistSessionNow,
  ]);

  const handleGridReady = useCallback((params) => {
    gridReadyRef.current = true;
    // Snapshot the column defs' own state before any layout / session restore,
    // so "Reset view" knows what an untouched table looks like. Read from the
    // event's api — gridRef.current.api isn't populated yet at onGridReady.
    const api = params?.api;
    if (api && !pristineColumnStateRef.current) {
      pristineColumnStateRef.current = columnStateSignature(
        api.getColumnState(),
      );
      // A hand resize (drag or double-click a header edge) counts as a change;
      // flex re-flows and programmatic widths don't.
      api.addEventListener("columnResized", (e) => {
        if (
          e?.finished &&
          (e.source === "uiColumnResized" || e.source === "autosizeColumns")
        ) {
          userResizedRef.current = true;
        }
      });
    }
    if (defaultAppliedRef.current) return;
    defaultAppliedRef.current = true;
    // Guard snapshots through the whole restore so the programmatic
    // applyColumnState calls don't clobber the saved snapshot.
    restoringSessionRef.current = true;
    const finishRestore = () =>
      setTimeout(() => {
        restoringSessionRef.current = false;
      }, 250);

    // "Where you left off" wins over the default layout when enabled + present.
    if (storage.getRememberSession()) {
      const session = storage.getLastSession();
      if (session) {
        setTimeout(() => {
          restoreSession(session);
          finishRestore();
        }, 100);
        return;
      }
    }

    const persistedDefaultId = storage.getDefaultId();
    if (!persistedDefaultId) {
      finishRestore();
      return;
    }
    const layout = storage
      .getLayouts()
      .find((l) => l.id === persistedDefaultId);
    if (layout) {
      setTimeout(() => {
        applyLayout(layout, { skipAutoDefault: true });
        finishRestore();
      }, 100);
    } else {
      finishRestore();
    }
  }, [storage, applyLayout, restoreSession]);

  const handleSaveLayout = useCallback(() => {
    const trimmed = saveDialogName.trim();
    if (!trimmed) return;
    const state = captureCurrentState();
    if (!state) return;
    const newLayout = {
      id: `layout_${storageKeyPrefix}_${size(layouts)}_${trimmed}`,
      name: trimmed,
      ...state,
    };
    const next = [...layouts, newLayout];
    persistLayouts(next);
    setActiveLayoutId(newLayout.id);
    // Newly created layout becomes the default.
    persistDefaultId(newLayout.id);
    setSaveDialogOpen(false);
    setSaveDialogName("");
  }, [
    saveDialogName,
    captureCurrentState,
    layouts,
    persistLayouts,
    storageKeyPrefix,
    persistDefaultId,
  ]);

  // Overwrite an existing saved view with the live grid state, keeping its id
  // and name (so the default-layout pointer and the active selection survive).
  const handleUpdateLayout = useCallback(
    (layoutId) => {
      const state = captureCurrentState();
      if (!state) return;
      const target = layouts.find((l) => l.id === layoutId);
      if (!target) return;
      persistLayouts(
        layouts.map((l) =>
          l.id === layoutId
            ? { ...l, ...state, updatedAt: new Date().toISOString() }
            : l,
        ),
      );
      setActiveLayoutId(layoutId);
      layoutMenuRef.current?.close();
    },
    [captureCurrentState, layouts, persistLayouts],
  );

  const handleApplyLayout = useCallback(
    (layout) => {
      applyLayout(layout);
      layoutMenuRef.current?.close();
    },
    [applyLayout],
  );

  const handleToggleDefault = useCallback(
    (layoutId) => {
      persistDefaultId(defaultId === layoutId ? null : layoutId);
    },
    [defaultId, persistDefaultId],
  );

  const handleDeleteLayout = useCallback(
    (layoutId) => {
      persistLayouts(layouts.filter((l) => l.id !== layoutId));
      if (defaultId === layoutId) {
        persistDefaultId(null);
      }
      if (activeLayoutId === layoutId) setActiveLayoutId(null);
    },
    [layouts, defaultId, activeLayoutId, persistLayouts, persistDefaultId],
  );

  const handleClearActiveLayout = useCallback(() => {
    const api = gridRef.current?.api;
    if (api) {
      api.resetColumnState();
      api.applyColumnState({ defaultState: { sort: null } });
      api.setFilterModel(null);
      // What reset produces IS the default view — re-baseline on it so the
      // button can't stay lit over a leftover the fingerprint still sees.
      pristineColumnStateRef.current = columnStateSignature(
        api.getColumnState(),
      );
    }
    userResizedRef.current = false;
    setIsViewChanged(false);
    setActiveSort(null);
    setActiveLayoutId(null);
    setQuickFilter(null);
    persistDefaultId(null);
    layoutMenuRef.current?.close();
  }, [persistDefaultId]);

  // Is there anything for "Reset view" to undo? A saved layout in play, a
  // search, a Sort-menu sort, header filters, or columns moved / hidden /
  // pinned / sized / grouped away from the column defs.
  const refreshViewChanged = useCallback(() => {
    const api = gridRef.current?.api;
    const gridChanged =
      !!api &&
      !!pristineColumnStateRef.current &&
      (userResizedRef.current ||
        !isEmpty(api.getFilterModel()) ||
        columnStateSignature(api.getColumnState()) !==
          pristineColumnStateRef.current);
    setIsViewChanged(
      !!activeLayoutId || !!quickFilter || !!activeSort || gridChanged,
    );
  }, [activeLayoutId, quickFilter, activeSort]);

  // The grid's stateUpdated fires a tick after the change, so it reads the
  // latest check through a ref rather than the closure it was handed.
  const refreshViewChangedRef = useRef(refreshViewChanged);
  useEffect(() => {
    refreshViewChangedRef.current = refreshViewChanged;
    refreshViewChanged();
  }, [refreshViewChanged]);

  const handleGridStateUpdated = useCallback(() => {
    persistSessionNow();
    refreshViewChangedRef.current();
  }, [persistSessionNow]);

  const activeLayoutName = useMemo(
    () => layouts.find((l) => l.id === activeLayoutId)?.name || null,
    [layouts, activeLayoutId],
  );

  // ── Navigate to loan on row/name-cell click (matches the LO pipeline) ──
  const onCellClicked = useCallback(
    (params) => {
      if (params?.colDef?.field === "loanNumber") return;
      const el = params.event?.target;
      if (
        (el instanceof Element && el.closest("button")) ||
        (el instanceof Element && el.closest("a"))
      )
        return;
      if (isNil(params?.data?.loanNumber)) return;
      if (params?.colDef?.field === "loanName")
        navigate(
          `${drilldownBaseForRow(params.data, drilldownBase)}/${params.data._id}`,
        );
    },
    [navigate, drilldownBase],
  );

  return (
    <Box
      sx={{
        background: "#ffffff",
        display: "flex",
        overflow: "hidden",
        height: "100%",
      }}
    >
      <ScreenContent
        sx={{
          height: "100%",
          p: 0,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        {/* ── Toolbar: search + sort + layout + export ── */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            p: 1,
            px: 1.2,
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Flx ac g={1} wrap>
            {/* {title ? (
              <Htag h4 sx={{ mr: 1 }}>
                {title}
              </Htag>
            ) : null} */}
            <SearchInput
              placeholder={`Search ${title ? title.toLowerCase() : "pipeline"}...`}
              sx={{
                width: "160px",
                ".MuiInputBase-root": {
                  borderRadius: "50px !important",
                  background: "#ffffff",
                  fontSize: "11px",
                  pl: 1.2,
                  pr: 1,
                  ".MuiSvgIcon-root": { fontSize: "11px" },
                },
                input: { height: "unset", fontSize: "11px" },
                fieldset: { border: "none" },
              }}
              value={quickFilter}
              onChange={setQuickFilter}
            />

            {/* Two stacked rows: Status on top, Sub-Status on its own row
                beneath it. Drilling into a status used to append its sub-status
                chips to the end of the same wrapping line, which buried them;
                each row still wraps internally when it runs out of room. */}
            <Flx column g={0.5} sx={{ ml: 0.5, py: 1 }}>
              <Flx ac wrap g={1}>
                {statusTabs ? (
                  <Flx ac g={0.5}>
                    <Flx ac sx={{ height: "max-content", p: 0.1 }}>
                      {/* <FilterAltOutlined
                      className="thin"
                      sx={{ fontSize: 14, color: "rgba(0, 0, 0, 0.6)" }}
                    /> */}
                      <Txt
                        secondary
                        sx={{
                          fontSize: 11,
                          ml: 0.5,
                          flexShrink: 0,
                          fontWeight: 600,
                          color: "#000000d6",
                        }}
                      >
                        Status:
                      </Txt>
                    </Flx>
                    <FilterTabSwitcher
                      value={statusFilter}
                      onChange={setStatusFilter}
                      tabs={statusTabs}
                      fontSize={11}
                      fontWeight={400}
                      activeFontWeight={500}
                      activeColor="#1F5AF2"
                      color="#181d1f"
                      backgroundColor="transparent"
                      inactiveCountColor={grey[600]}
                      buttonPadding={{ px: 1, py: 0.6 }}
                      sx={{
                        flexWrap: "wrap",
                        gap: 0,
                        p: 0,
                        ".selected": {
                          boxShadow: "none",
                          border: "1px solid #1a56db4a",
                          borderRadius: "4px",
                        },
                      }}
                    />
                  </Flx>
                ) : null}
              </Flx>

              {/* Sub-Status row — only rendered once a status with sub-statuses
                  is selected, so the toolbar stays a single row otherwise. */}
              {subStatusTabs ? (
                <Flx ac wrap g={0.5}>
                  <Flx ac sx={{ height: "max-content", p: 0.1 }}>
                    <SubdirectoryArrowRight
                      sx={{ fontSize: 13, color: "rgba(0, 0, 0, 0.6)" }}
                    />
                    <Txt
                      secondary
                      sx={{
                        fontSize: 11,
                        ml: 0.5,
                        flexShrink: 0,
                        fontWeight: 600,
                        color: "#000000d6",
                      }}
                    >
                      {statusFilter} Sub-Status:
                    </Txt>
                  </Flx>
                  <FilterTabSwitcher
                    value={subStatusFilter}
                    onChange={setSubStatusFilter}
                    tabs={subStatusTabs}
                    fontSize={11}
                    fontWeight={400}
                    activeFontWeight={500}
                    activeColor="#1F5AF2"
                    color="#181d1f"
                    backgroundColor="transparent"
                    inactiveCountColor={grey[600]}
                    buttonPadding={{ px: 1, py: 0.6 }}
                    sx={{
                      flexWrap: "wrap",
                      gap: 0,
                      p: 0,
                      ".selected": {
                        boxShadow: "none",
                        border: "1px solid #1a56db4a",
                        borderRadius: "4px",
                      },
                    }}
                  />
                </Flx>
              ) : null}
            </Flx>
          </Flx>

          <Flx
            ac
            g={0.8}
            wrap
            sx={{
              // Compact toolbar: 11px button labels with proportionate icons.
              "& .MuiButton-root": { fontSize: "11px", py: "4px" },
              "& .MuiSvgIcon-root": { fontSize: "14px !important" },
            }}
          >
            {/* Reset view — back to the column defs' table (no layout, search,
                sort or header filters). Disabled while there's nothing to undo,
                theme-colored once the view has changed. */}
            <Tooltip
              title={
                isViewChanged
                  ? "Reset columns, filters, sort and search to the default table"
                  : "The table is already in its default view"
              }
            >
              <span>
                <Button
                  size="medium"
                  variant="text"
                  disabled={!isViewChanged}
                  startIcon={<ReplayRounded sx={{ ml: 0.4 }} />}
                  onClick={handleClearActiveLayout}
                  sx={{ textTransform: "none", px: 1, borderRadius: "8px" }}
                >
                  Reset view
                </Button>
              </span>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />

            <Tooltip
              title={
                userEmail
                  ? "Show only loans you're assigned to (any role or permitted user)"
                  : "No signed-in user email available"
              }
            >
              <span>
                <Button
                  size="medium"
                  variant={showOnlyMine ? "contained" : "outlined"}
                  color={showOnlyMine ? "primary" : "gray"}
                  disabled={!userEmail}
                  startIcon={
                    showOnlyMine ? (
                      <CheckRounded sx={{ ml: 0.4 }} />
                    ) : (
                      <PersonOutlineOutlined sx={{ ml: 0.4 }} />
                    )
                  }
                  onClick={() =>
                    setShowOnlyMine((v) => {
                      const next = !v;
                      storage.setShowOnlyMine(next);
                      return next;
                    })
                  }
                  sx={{
                    border: "1px solid #e0e0e0",
                    fontWeight: showOnlyMine ? 600 : 400,
                    textTransform: "none",
                    px: 1,
                    borderRadius: "8px",
                  }}
                >
                  Show only my loans
                  <Box
                    component="span"
                    sx={{
                      pl: 0.5,
                      color: showOnlyMine ? "inherit" : "#94a3b8",
                      opacity: showOnlyMine ? 0.85 : 1,
                    }}
                  >
                    ({myLoansCount})
                  </Box>
                </Button>
              </span>
            </Tooltip>

            <ButtonMenu
              menuTitle="Sort Options"
              options={SORT_MENU_OPTIONS}
              value={activeSortValue}
              startIcon={<SwapVertOutlined sx={{ ml: 0.4 }} />}
              onChange={handleSortChange}
              buttonText={activeSortValue ? null : "Sort: None"}
              allowDeselect
              showBadge={activeSortValue !== null}
              menuMinWidth={280}
            />

            <Badge color="primary" variant="dot" invisible={!activeLayoutId}>
              <Button
                ref={layoutAnchorRef}
                startIcon={
                  <ViewComfyOutlined
                    className="thin"
                    sx={{ fontSize: "16px !important", ml: 0.5 }}
                  />
                }
                size="medium"
                color={activeLayoutId ? "primary" : "gray"}
                endIcon={<KeyboardArrowDown />}
                onClick={() => layoutMenuRef.current?.toggle()}
                variant="outlined"
                sx={{
                  border: "1px solid #e0e0e0",
                  fontWeight: activeLayoutId ? 600 : 400,
                  textTransform: "none",
                  px: 1.2,
                  borderRadius: "8px",
                }}
              >
                {activeLayoutName
                  ? `Layout: ${activeLayoutName}`
                  : "Layout: Default"}
              </Button>
            </Badge>
            <StyledMenu
              ref={layoutMenuRef}
              anchorRef={layoutAnchorRef}
              title="Table Layouts"
              description="Save and manage your preferred table views."
              minWidth={300}
              maxWidth={300}
            >
              <Flx ac jb g={1} sx={{ p: 1.5 }}>
                <Button
                  size="small"
                  // variant="outlined"
                  color="primary"
                  sx={{ flexGrow: 1 }}
                  startIcon={<AddRounded fontSize="small" />}
                  onClick={() => {
                    setSaveDialogName("");
                    setSaveDialogOpen(true);
                    layoutMenuRef.current?.close();
                  }}
                >
                  Save Current Table Layout
                  {/* Save Current Table As Layout */}
                </Button>
              </Flx>
              <Divider />

              {isEmpty(layouts) ? (
                <Box
                  sx={{
                    p: 2,
                    width: 300,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Txt secondary sx={{ fontSize: 12, textAlign: "center" }}>
                    No saved layouts yet. Configure the columns, filters, and
                    sort, then save the view.
                  </Txt>
                </Box>
              ) : (
                <Box sx={{ py: 0.5 }}>
                  {layouts.map((layout) => {
                    const isActive = activeLayoutId === layout.id;
                    const isDefault = defaultId === layout.id;
                    return (
                      <MenuItem
                        key={layout.id}
                        selected={isActive}
                        onClick={() => handleApplyLayout(layout)}
                        sx={{
                          py: 1,
                          px: 1.5,
                          mx: 0.75,
                          borderRadius: "8px",
                        }}
                      >
                        <Flx ac jb g={1} sx={{ width: "100%" }}>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: isActive ? 600 : 400,
                              color: isActive ? "#1d4ed8" : "#374151",
                            }}
                          >
                            {layout.name}
                          </Typography>
                          <Flx ac g={0.5}>
                            <Tooltip title="Update this layout with the current columns, filters and sort">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateLayout(layout.id);
                                }}
                              >
                                <SaveAsOutlined
                                  sx={{ fontSize: 17, color: "#9ca3af" }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                isDefault
                                  ? "Default — applied on load"
                                  : "Set as default"
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleDefault(layout.id);
                                }}
                              >
                                {isDefault ? (
                                  <StarRounded
                                    sx={{ fontSize: 18, color: "#f59e0b" }}
                                  />
                                ) : (
                                  <StarOutlineRounded
                                    sx={{ fontSize: 18, color: "#d1d5db" }}
                                  />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete layout">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteLayout(layout.id);
                                }}
                              >
                                <DeleteOutline
                                  sx={{ fontSize: 18, color: "#9ca3af" }}
                                />
                              </IconButton>
                            </Tooltip>
                          </Flx>
                        </Flx>
                      </MenuItem>
                    );
                  })}
                </Box>
              )}

              {/* ── Remember where I left off ── */}
              <Divider />
              <Box sx={{ px: 1.5, py: 1.25 }}>
                <FormControlLabel
                  sx={{
                    m: 0,
                    alignItems: "flex-start",
                    gap: 1,
                    ".MuiFormControlLabel-label": { mt: 0.25 },
                  }}
                  control={
                    <Switch
                      size="small"
                      checked={rememberSession}
                      onChange={handleToggleRememberSession}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                        Remember table view
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#6b7280" }}>
                        Saves your current table view so it opens exactly as you
                        left it.
                      </Typography>
                    </Box>
                  }
                />
              </Box>

            </StyledMenu>

            <DownloadDropdownButton />
          </Flx>
        </Box>

        {/* ── Table (always compact) ── wrapped so the slow-load prompt can
            overlay just below AG Grid's centered loading spinner. */}
        <Box sx={{ position: "relative", height: "100%", minHeight: 0 }}>
          <LoanOfficerPipelineTable
            gridRef={gridRef}
            loading={loading}
            rowData={filteredRowData}
            quickFilter={quickFilter}
            columnDefs={columnDefs}
            loanNameMinWidth={loanNameMinWidth}
            drilldownBase={drilldownBase}
            extraColumnDefs={extraColumnDefs}
            onCellClicked={onCellClicked}
            onGridReady={handleGridReady}
            onStateUpdated={handleGridStateUpdated}
            isCompact
          />
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: "calc(50% + 34px)",
                left: 0,
                right: 0,
                display: "flex",
                justifyContent: "center",
                pointerEvents: "none",
                zIndex: 2,
              }}
            >
              <Box sx={{ pointerEvents: "auto" }}>
                <SlowRequestPrompt
                  context={`the ${title ? title.toLowerCase() : "pipeline"}`}
                  details={{ Board: title || storageKeyPrefix }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </ScreenContent>

      {/* ── Save Layout Dialog ── */}
      <BasicModal
        show={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="xs"
        title="Save Table Layout"
      >
        <Box>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Layout name"
            value={saveDialogName}
            onChange={(e) => setSaveDialogName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSaveLayout();
            }}
          />
          <Flx ac end g={1} sx={{ mt: 2 }}>
            <Button
              variant="text"
              color="gray"
              onClick={() => setSaveDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveLayout}
              disabled={!saveDialogName.trim()}
            >
              Save
            </Button>
          </Flx>
        </Box>
      </BasicModal>
    </Box>
  );
};

export default PipelineTableScreen;
